import { Image as ImageIcon, Music as MusicIcon, Type as TypeIcon, Video as VideoIcon } from 'lucide-react';
import { useI18n } from '@/providers/i18n';
import { ReminderDialog, type ReminderAction } from './reminder-dialog';

export type PrecheckMissing = 'prompt' | 'image' | 'image2' | 'video' | 'audio' | (string & {});

type PrecheckDialogProps = {
  /** 缺失的输入项；null 表示关闭。支持 'prompt' | 'image' | 'video' | 'audio' 以及 'a|b' 或条件。 */
  missing: PrecheckMissing | null;
  onOpenChange: (open: boolean) => void;
  /**
   * 可选主操作按钮（如图像节点的「添加图片」）。
   * 提供时渲染「取消 + 操作」两个按钮；不提供时渲染单个「确认」按钮。
   */
  action?: { label: string; onClick: () => void };
};

/**
 * 生成前 precheck 缺失输入的统一提醒弹窗，图像/视频节点共用。
 */
export function PrecheckDialog({ missing, onOpenChange, action }: PrecheckDialogProps) {
  const { t } = useI18n();
  const isAlts = Boolean(missing && missing.includes('|'));

  const icon =
    missing === 'image' || missing === 'image2' ? (
      <ImageIcon size={16} className="text-primary" />
    ) : missing === 'video' ? (
      <VideoIcon size={16} className="text-primary" />
    ) : missing === 'audio' ? (
      <MusicIcon size={16} className="text-primary" />
    ) : (
      <TypeIcon size={16} className="text-primary" />
    );

  const title =
    missing === 'image'
      ? t('Image required')
      : missing === 'image2'
        ? t('Two images required')
        : missing === 'video'
        ? t('Video required')
        : missing === 'audio'
          ? t('Audio required')
          : isAlts
            ? t('Input required')
            : t('Prompt required');

  const description =
    missing === 'image'
      ? t('This model needs an input image to generate. Please connect or upload an image first.')
      : missing === 'image2'
        ? t('First & last frame mode requires two images. Please connect or upload both the first and last frame.')
        : missing === 'video'
        ? t('This model needs an input video to generate. Please connect or upload a video first.')
        : missing === 'audio'
          ? t('This model needs input audio to generate. Please connect or upload audio first.')
          : isAlts
            ? t('Please input {{items}}', { items: String(missing).split('|').join(' / ') })
            : t('This model needs a prompt to generate. Please enter a prompt first.');

  const actions: ReminderAction[] = action
    ? [
        { label: t('Cancel'), variant: 'outline' },
        { label: action.label, onClick: action.onClick },
      ]
    : [{ label: t('Confirm') }];

  return (
    <ReminderDialog
      open={missing !== null}
      onOpenChange={onOpenChange}
      icon={icon}
      title={title}
      description={description}
      actions={actions}
    />
  );
}
